import { api } from "./api";
import type { BulkSyncResult } from "@/types/bulkSync";

/**
 * The envelope a paginated list comes back in. Declared here the way
 * dailyLoggingService declares its own — there is no shared type, and the
 * interceptor keeps `{data, meta}` intact whenever `meta` is present, so a
 * service that types a list as a bare array compiles and then hands the page
 * an object.
 */
interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number };
}

/**
 * A flock's daily measurements that are not production: what the birds weigh,
 * how big the eggs are, how much water went in, and what the shed was like.
 *
 * EVERY MEASURE IS NULLABLE, and null means "not measured" — which is not the
 * same as zero and must never be sent as zero. A farm that weighs a sample on
 * Mondays and nothing else writes one field and leaves six null; an average
 * over rows that treat an unmeasured field as 0 is wrong in a way nobody
 * notices.
 *
 * The write is an UPSERT on (flock, date): re-submitting a day corrects it. A
 * field left out is CLEARED, deliberately — on a form showing all seven boxes,
 * leaving one empty has to mean "there is no reading", or a mistyped weight
 * could never be erased. The server refuses a submission carrying no measure
 * at all, because that would silently wipe a day that had readings.
 */
export interface FlockDailyMetrics {
  id: number;
  flock_id: number;
  date: string;
  avg_body_weight_g: number | null;
  avg_egg_weight_g: number | null;
  water_litres: number | null;
  light_hours: number | null;
  temp_min_c: number | null;
  temp_max_c: number | null;
  humidity_pct: number | null;
  notes: string | null;
  flock_name?: string;
}

/**
 * What a bulk save answers with.
 *
 * An alias, not a second declaration: this used to be its own interface,
 * structurally identical to BulkSyncResult, and two names for one wire format
 * drift. `success_count` and `failure_count` both matter here — a partial
 * success is the NORMAL case, because one shed's mistyped reading is rejected
 * on its own and the rest are written.
 */
export type BulkMetricsResult = BulkSyncResult<FlockDailyMetrics>;

export interface SaveFlockMetrics {
  flock_id: number;
  date: string;
  avg_body_weight_g: number | null;
  avg_egg_weight_g: number | null;
  water_litres: number | null;
  light_hours: number | null;
  temp_min_c: number | null;
  temp_max_c: number | null;
  humidity_pct: number | null;
  notes: string | null;
}

/** The seven measures, with the units the breeder guides publish them in. */
export const METRIC_FIELDS = [
  {
    key: "avg_body_weight_g" as const,
    label: "Average body weight",
    unit: "g",
    hint: "Grams per bird, from a weighed sample. Read against the breed's body-weight curve — the earliest warning a farm gets.",
    step: "1",
  },
  {
    key: "avg_egg_weight_g" as const,
    label: "Average egg weight",
    unit: "g",
    hint: "Grams per egg.",
    step: "0.1",
  },
  {
    key: "water_litres" as const,
    label: "Water",
    unit: "L",
    hint: "Read against feed rather than alone: a water-to-feed ratio moving is an earlier signal than either number.",
    step: "1",
  },
  {
    key: "light_hours" as const,
    label: "Light hours",
    unit: "h",
    hint: "Cutting light for laying birds lowers production.",
    step: "0.5",
  },
  {
    key: "temp_min_c" as const,
    label: "Shed temperature, low",
    unit: "°C",
    hint: "",
    step: "0.1",
  },
  {
    key: "temp_max_c" as const,
    label: "Shed temperature, high",
    unit: "°C",
    hint: "",
    step: "0.1",
  },
  {
    key: "humidity_pct" as const,
    label: "Humidity",
    unit: "%",
    hint: "",
    step: "1",
  },
];

export type MetricKey = (typeof METRIC_FIELDS)[number]["key"];

export const flockMetricsService = {
  list: (
    params: { flock_id?: number; from?: string; to?: string; limit?: number } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.flock_id) q.set("flock_id", String(params.flock_id));
    if (params.from) q.set("from", params.from);
    if (params.to) q.set("to", params.to);
    // Callers charting a flock's history MUST pass one: the server defaults to
    // 100, newest first, and 100 rows of a daily round sheet is about three
    // months — a growth curve would silently begin at week 8 of a 20-week
    // rearing with nothing on screen to say so.
    //
    // 500 is the CEILING, and asking for more does not clamp: the service
    // treats `limit > 500` as invalid and falls back to 100 (see
    // flock_daily_metrics_service.go). So a caller that raises this expecting
    // to be capped gets FEWER rows, silently, and any "truncated" heuristic
    // based on the requested number stops firing.
    if (params.limit) q.set("limit", String(params.limit));
    const qs = q.toString();
    // PaginatedResponse, not a bare array. The handler answers with
    // RespondPaginated, and the axios interceptor keeps `{data, meta}` intact
    // whenever a `meta` key is present — so typing this as an array compiled
    // fine and handed the page an object. `rows.map is not a function`, on
    // load, every time. Caught by the form E2E; no unit test would have.
    return api
      .get<PaginatedResponse<FlockDailyMetrics>>(`/flock-daily-metrics${qs ? `?${qs}` : ""}`)
      .then((r) => r.data.data);
  },

  /** What is already recorded for this flock on this day, so a form can
   *  prefill rather than silently overwrite. 404 when nothing was measured. */
  forFlockDate: (flockId: number, date: string) =>
    api.get<FlockDailyMetrics>(`/flock-daily-metrics/${flockId}/${date}`).then((r) => r.data),

  save: (data: SaveFlockMetrics) =>
    api.post<FlockDailyMetrics>("/flock-daily-metrics", data).then((r) => r.data),

  /**
   * One round trip for a whole day's sheds, which is what the Daily Report
   * needs: a wizard submitting five flocks one at a time turns one "Submit"
   * into five chances to half-succeed.
   *
   * Per-row outcomes, NOT an all-or-nothing error — a body weight typed in
   * kilograms for one shed must not discard the other four sheds' readings.
   * `failed[].index` points back into the array as it was SENT, so the caller
   * can name the flock that was rejected.
   */
  bulkSave: (rows: SaveFlockMetrics[]) =>
    api
      .post<BulkMetricsResult>("/flock-daily-metrics/bulk", rows)
      .then((r) => r.data),

  remove: (id: number) => api.delete(`/flock-daily-metrics/${id}`).then((r) => r.data),
};

/**
 * An empty box is null, not 0.
 *
 * `Number("")` is 0, which is the whole trap: a blank field would silently
 * record a body weight of zero grams. Anything that is not a finite number
 * becomes null and is stored as "not measured".
 */
export function toMeasure(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** True when nothing at all was entered — the server refuses this, and the
 *  form should say so before a round trip. */
export function isEmptySubmission(m: Pick<SaveFlockMetrics, MetricKey>): boolean {
  return METRIC_FIELDS.every((f) => m[f.key] == null);
}
